import { z } from "zod";
import { v4 as uuidv4 } from "uuid";

import { createAttribute } from "@coltorapps/builder";
import { dateSchema } from "./date";
import { dropdownSchema } from "./dropdown";
import { inputSchema } from "./input";
import { numberSchema } from "./number";
import { rowExpresssionSchema } from "./row-expression";
import { ExpressionDefault, ExpressionType } from "@/const/expression";

const gridHeaderItemSchema = z.discriminatedUnion("type", [
  inputSchema,
  numberSchema,
  dateSchema,
  dropdownSchema,
  rowExpresssionSchema,
]);

const gridHeaderArraySchema = z
  .array(gridHeaderItemSchema)
  .min(1)
  .superRefine((headers, ctx) => {
    const seen = new Map<string, number>();

    headers.forEach((header, index) => {
      const normalizedKeyValue = header.keyValue.trim();
      const duplicateAt = seen.get(normalizedKeyValue);

      if (duplicateAt === undefined) {
        seen.set(normalizedKeyValue, index);
        return;
      }

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [duplicateAt, "keyValue"],
        message: `Duplicate column keyValue: "${normalizedKeyValue}"`,
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [index, "keyValue"],
        message: `Duplicate column keyValue: "${normalizedKeyValue}"`,
      });
    });
  });

export const gridHeaderAttribute = createAttribute({
  name: "gridHeaders",
  validate(value) {
    const normalizedValue = z.preprocess((val) => {
      if (!Array.isArray(val)) return val;

      return val.map((item, index) => {
        if (typeof item === "string") {
          const label = item.trim() || `Column ${index + 1}`;
          return {
            label,
            keyValue: `column_${index + 1}`,
            key: uuidv4(),
            type: "input",
            placeholder: "",
            defaultValue: "",
            required: false,
            flowType: ["split"],
          };
        }

        if (!item || typeof item !== "object") {
          return {
            label: `Column ${index + 1}`,
            keyValue: `column_${index + 1}`,
            key: uuidv4(),
            type: "input",
            placeholder: "",
            defaultValue: "",
            required: false,
            flowType: ["split"],
          };
        }

        const record = item as Record<string, unknown>;
        const rawLabel = record.label;
        const rawKey = record.key;
        const rawKeyValue = record.keyValue;
        const type =
          typeof record.type === "string" &&
          ["input", "number", "date", "dropdown", "row-expression"].includes(
            record.type,
          )
            ? record.type
            : "input";

        return {
          label:
            typeof rawLabel === "string" && rawLabel.trim().length > 0
              ? rawLabel
              : `Column ${index + 1}`,
          keyValue:
            typeof rawKeyValue === "string" && rawKeyValue.trim().length > 0
              ? rawKeyValue
              : `column_${index + 1}`,
          key:
            typeof rawKey === "string" && rawKey.trim().length > 0
              ? rawKey
              : uuidv4(),
          type,
          subtype:
            type === "date"
              ? typeof record.subtype === "string" &&
                ["date", "time", "datetime"].includes(record.subtype)
                ? record.subtype
                : "date"
              : undefined,
          placeholder:
            typeof record.placeholder === "string" ? record.placeholder : "",
          defaultValue:
            record.defaultValue === undefined || record.defaultValue === null
              ? ""
              : String(record.defaultValue),
          required:
            typeof record.required === "boolean" ? record.required : false,
          searchInOptions:
            type === "dropdown" && typeof record.searchInOptions === "boolean"
              ? record.searchInOptions
              : false,
          flowType: Array.isArray(record.flowType)
            ? record.flowType
            : ["split"],
          datasource:
            record.datasource &&
            typeof record.datasource === "object" &&
            "type" in record.datasource
              ? record.datasource
              : undefined,
          expression:
            type === "row-expression"
              ? typeof record.expression === "string"
                ? record.expression
                : ExpressionDefault[ExpressionType.GridRow]
              : undefined,
        };
      });
    }, gridHeaderArraySchema);

    return normalizedValue.parse(value);
  },
});
